/**
 * @author Nuwandi Hewamadduma
 * @version 0.0.1
 * @since 23/4/2025
 */

import React, { useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { Button } from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import PrintIcon from "@mui/icons-material/Print";

const ApplicationReportTable = ({ applicationReportData, isLoading }) => {
  const columns = useMemo(
    () => [
      {
        accessorKey: "applicationId",
        header: "Application ID",
        size: 50,
      },
      {
        accessorKey: "appName",
        header: "App Name",
        size: 50,
      },
      {
        accessorKey: "appVersion",
        header: "App Ver",
        size: 50,
      },
      {
        accessorKey: "appId",
        header: "Package ID",
        size: 50,
      },
      {
        accessorKey: "versionCode",
        header: "Version Code",
        size: 50,
      },
      {
        accessorKey: "orgCode",
        header: "Org Code",
        size: 50,
      },
      {
        accessorKey: "uploadOn",
        header: "Upload On",
        size: 50,
      },
      {
        accessorKey: "appSummaryDtl.totTerminals",
        header: "Tot Terminals",
        size: 50,
      },
      {
        accessorKey: "appSummaryDtl.totInstallCount",
        header: "Tot Install Count",
        size: 50,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 50,
      },
      {
        accessorKey: "createdDateTime",
        header: "Created",
        size: 50,
      },
    ],
    []
  );

  const isNumeric = (value) => {
    return !isNaN(value) && value !== null && value !== "" && typeof value !== "boolean";
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "A4",
    });

    doc.setFontSize(16);
    doc.text("Application Report", 40, 40);

    const tableColumn = columns.map((col) => col.header);
    const tableRows = applicationReportData.map((row) =>
      columns.map((col) => {
        const accessorKeys = col.accessorKey.split(".");
        let value = row;
        for (let key of accessorKeys) {
          value = value?.[key];
        }
        return value !== undefined && value !== null ? String(value) : "N/A";
      })
    );

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 60,
      margin: { left: 40, right: 40 },
      styles: {
        fontSize: 8,
        cellPadding: 4,
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [22, 160, 133],
        textColor: 255,
        fontStyle: "bold",
      },
    });

    doc.save("application_report.pdf");
  };

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Application Report");

    worksheet.columns = [
      { header: "APP NAME", key: "appName", width: 55 },
      { header: "APPLICATION ID", key: "applicationId", width: 16 },
      { header: "APP VER", key: "appVer", width: 15 },
      { header: "PACKAGE ID", key: "appId", width: 38 },
      { header: "VERSION CODE", key: "versionCode", width: 15 },
      { header: "ORG CODE", key: "orgCode", width: 15 },
      { header: "UPLOAD ON", key: "uploadOn", width: 30 },
      { header: "TOT TERMINALS", key: "totTerminals", width: 18 },
      { header: "TOT INSTALL COUNT", key: "totInstallCount", width: 20 },
      { header: "STATUS", key: "status", width: 20 },
      { header: "DESCRIPTION", key: "description", width: 35 },
      { header: "CREATED", key: "createdDateTime", width: 24 },
    ];

    applicationReportData.forEach((item) => {
      worksheet.addRow({
        appName: item.appName,
        applicationId: item.applicationId,
        appVer: item.appVersion,
        appId: item.appId,
        versionCode: item.versionCode,
        orgCode: item.orgCode,
        uploadOn: item.uploadOn,
        totTerminals: item.appSummaryDtl?.totTerminals,
        totInstallCount: item.appSummaryDtl?.totInstallCount,
        status: item.status,
        description: item.description,
        createdDateTime: item.createdDateTime,
      });
    });

    worksheet.getRow(1).font = { bold: true };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, "application_report.xlsx");
  };

  return (
    <MaterialReactTable
      columns={columns}
      data={applicationReportData ?? []}
      state={{
        showProgressBars: isLoading,
        showSkeletons: isLoading,
      }}
      muiTableContainerProps={{
        sx: {
          overflowX: "auto",
          paddingLeft: "1rem",
          paddingRight: "1rem",
        },
      }}
      muiTableBodyCellProps={({ cell }) => ({
        sx:
          applicationReportData.length > 0
            ? {
                wordWrap: "break-word",
                whiteSpace: "normal",
                maxWidth: "160px",
                textAlign: isNumeric(cell.getValue()) ? "right" : "left",
              }
            : {},
      })}
      muiLinearProgressProps={{
        sx: { position: "absolute", bottom: 0, left: 0, right: 0 },
      }}
      enableColumnActions={false}
      enableSorting={false}
      enablePagination={true}
      initialState={{
        pagination: { pageSize: 10, pageIndex: 0 },
      }}
      positionActionsColumn="last"
      muiTableBodyRowProps={({ row }) => ({
        onClick: () => console.log("Row clicked:", row.original),
        sx: { cursor: "pointer" },
      })}
      renderTopToolbarCustomActions={({ table }) => (
        <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
          <Button variant="contained" color="primary" startIcon={<PrintIcon />} onClick={handleExportPDF} sx={{ marginLeft: 2 }}>
            PDF
          </Button>
          <Button variant="contained" color="success" startIcon={<TableChartIcon />} onClick={handleExportExcel} sx={{ marginLeft: 2 }}>
            XLSX
          </Button>
        </div>
      )}
      renderEmptyRowsFallback={() => <div style={{ padding: "2rem", textAlign: "center", width: "100%" }}>No application report data to display.</div>}
    />
  );
};

export default ApplicationReportTable;
